import { useMemo, useState, useEffect, useRef, useCallback } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { useNavigate } from "react-router-dom";
import { EmployeeWithProfile } from "@/pages/OrgChartPage";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";
import { Building2, Move, Loader2 } from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { TooltipProvider } from "@/components/ui/tooltip";
import {
  Department, CanvasItem, Connection, ConnectionSide, ToolMode,
  NODE_WIDTH, NODE_HEIGHT, EMPLOYEE_NODE_HEIGHT, GRID_SIZE, SNAP_THRESHOLD,
  ALIGNMENT_THRESHOLD, CANVAS_MIN_WIDTH, CANVAS_MIN_HEIGHT, CANVAS_PADDING,
  MIN_ZOOM, MAX_ZOOM, ZOOM_STEP,
} from "./orgchart/types";
import { OrgChartSidebar } from "./orgchart/OrgChartSidebar";
import { OrgChartToolbar } from "./orgchart/OrgChartToolbar";
import { DepartmentCard, EmployeeCard } from "./orgchart/OrgChartNodeCard";
import { OrgChartConnections } from "./orgchart/OrgChartConnections";

interface OrgChartFreeformProps {
  employees: EmployeeWithProfile[];
  onEmployeeUpdate: () => void;
}

export function OrgChartFreeform({ employees, onEmployeeUpdate }: OrgChartFreeformProps) {
  const { user, userRole } = useAuth();
  const isEmployee = userRole === 'employee' || userRole === 'hr_clerk';
  const navigate = useNavigate();

  // Data state
  const [departments, setDepartments] = useState<Department[]>([]);
  const [canvasItems, setCanvasItems] = useState<CanvasItem[]>([]);
  const [connections, setConnections] = useState<Connection[]>([]);

  // UI state
  const [toolMode, setToolMode] = useState<ToolMode>('move');
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deptToDelete, setDeptToDelete] = useState<Department | null>(null);
  const [showHelp, setShowHelp] = useState(false);
  const [snapToGrid, setSnapToGrid] = useState(true);

  // Editing state
  const [editingEmployeeId, setEditingEmployeeId] = useState<string | null>(null);
  const [editPosition, setEditPosition] = useState("");
  const [editingTasksId, setEditingTasksId] = useState<string | null>(null);
  const [editTasksText, setEditTasksText] = useState("");

  // Drag state
  const [isDragging, setIsDragging] = useState(false);
  const [dragItemId, setDragItemId] = useState<string | null>(null);
  const dragOffsetRef = useRef({ x: 0, y: 0 });

  // Pan state (spacebar + drag)
  const [isPanning, setIsPanning] = useState(false);
  const [isSpaceDown, setIsSpaceDown] = useState(false);
  const panStartRef = useRef({ x: 0, y: 0, scrollLeft: 0, scrollTop: 0 });

  // Connection state
  const [connectingFrom, setConnectingFrom] = useState<{ id: string; side: ConnectionSide } | null>(null);

  // Alignment guides
  const [alignmentGuides, setAlignmentGuides] = useState<{ x: number | null; y: number | null }>({ x: null, y: null });

  // Zoom
  const [zoom, setZoom] = useState(1);

  // Save state
  const [isSaving, setIsSaving] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [layoutId, setLayoutId] = useState<string | null>(null);

  const canvasRef = useRef<HTMLDivElement>(null);
  const canvasContainerRef = useRef<HTMLDivElement>(null);

  // --- Data Loading ---
  useEffect(() => { if (user) loadLayout(); }, [user]);
  useEffect(() => { loadDepartments(); }, []);

  const loadLayout = async () => {
    if (!user) return;
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from("orgchart_layouts").select("*").eq("user_id", user.id).maybeSingle();
      if (error) { console.error(error); return; }
      if (data) {
        setLayoutId(data.id);
        setCanvasItems((data.canvas_items as unknown as CanvasItem[]) || []);
        setConnections((data.connections as unknown as Connection[]) || []);
        setZoom(Number(data.zoom_level) || 1);
      }
    } finally { setIsLoading(false); }
  };

  const loadDepartments = async () => {
    const { data } = await supabase.from("departments").select("*").order("sort_order");
    setDepartments(data || []);
  };

  // --- Save ---
  const handleSaveLayout = async () => {
    if (!user) return;
    setIsSaving(true);
    try {
      const layoutData = {
        user_id: user.id,
        canvas_items: JSON.parse(JSON.stringify(canvasItems)),
        connections: JSON.parse(JSON.stringify(connections)),
        zoom_level: zoom,
      };
      if (layoutId) {
        const { error } = await supabase.from("orgchart_layouts").update(layoutData).eq("id", layoutId);
        if (error) throw error;
      } else {
        const { data, error } = await supabase.from("orgchart_layouts").insert(layoutData).select("id").single();
        if (error) throw error;
        setLayoutId(data.id);
      }
      setHasUnsavedChanges(false);
      toast.success("Organigramm gespeichert");
    } catch { toast.error("Fehler beim Speichern"); }
    finally { setIsSaving(false); }
  };

  useEffect(() => {
    if (!isLoading && (canvasItems.length > 0 || connections.length > 0)) setHasUnsavedChanges(true);
  }, [canvasItems, connections, zoom]);

  // --- Keyboard ---
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      if (e.code === 'Space' && !isSpaceDown) {
        e.preventDefault();
        setIsSpaceDown(true);
      }
      if (e.key === 'Escape') { setSelectedItemId(null); setConnectingFrom(null); }
      else if (e.key === 'm' || e.key === 'M') { setToolMode('move'); setConnectingFrom(null); }
      else if (e.key === 's' || e.key === 'S') { setToolMode('select'); setConnectingFrom(null); }
      else if (e.key === 'c' || e.key === 'C') { setToolMode('connect'); }
    };
    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.code === 'Space') { setIsSpaceDown(false); setIsPanning(false); }
    };
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    return () => { window.removeEventListener('keydown', handleKeyDown); window.removeEventListener('keyup', handleKeyUp); };
  }, [isSpaceDown]);

  // --- Canvas size ---
  const itemsOnCanvas = useMemo(() => new Set(canvasItems.map(i => i.id)), [canvasItems]);

  const canvasSize = useMemo(() => {
    let maxX = CANVAS_MIN_WIDTH, maxY = CANVAS_MIN_HEIGHT;
    canvasItems.forEach(item => {
      const h = item.type === 'department' ? NODE_HEIGHT : EMPLOYEE_NODE_HEIGHT;
      maxX = Math.max(maxX, item.x + NODE_WIDTH + CANVAS_PADDING);
      maxY = Math.max(maxY, item.y + h + CANVAS_PADDING);
    });
    return { width: maxX, height: maxY };
  }, [canvasItems]);

  // --- Snap to grid & alignment ---
  const snapPosition = useCallback((x: number, y: number, itemId: string): { x: number; y: number } => {
    let snappedX = x;
    let snappedY = y;
    let guideX: number | null = null;
    let guideY: number | null = null;

    if (snapToGrid) {
      snappedX = Math.round(x / GRID_SIZE) * GRID_SIZE;
      snappedY = Math.round(y / GRID_SIZE) * GRID_SIZE;
    }

    // Alignment to other items
    const currentItem = canvasItems.find(i => i.id === itemId);
    if (!currentItem) return { x: snappedX, y: snappedY };

    const currentW = NODE_WIDTH;
    const currentH = currentItem.type === 'department' ? NODE_HEIGHT : EMPLOYEE_NODE_HEIGHT;

    for (const other of canvasItems) {
      if (other.id === itemId) continue;
      const otherH = other.type === 'department' ? NODE_HEIGHT : EMPLOYEE_NODE_HEIGHT;

      // Center X alignment
      const otherCenterX = other.x + NODE_WIDTH / 2;
      const myCenterX = snappedX + currentW / 2;
      if (Math.abs(myCenterX - otherCenterX) < ALIGNMENT_THRESHOLD) {
        snappedX = otherCenterX - currentW / 2;
        guideX = otherCenterX;
      }
      // Left alignment
      if (Math.abs(snappedX - other.x) < ALIGNMENT_THRESHOLD) {
        snappedX = other.x;
        guideX = other.x;
      }
      // Right alignment
      if (Math.abs((snappedX + currentW) - (other.x + NODE_WIDTH)) < ALIGNMENT_THRESHOLD) {
        snappedX = other.x + NODE_WIDTH - currentW;
        guideX = other.x + NODE_WIDTH;
      }

      // Center Y alignment
      const otherCenterY = other.y + otherH / 2;
      const myCenterY = snappedY + currentH / 2;
      if (Math.abs(myCenterY - otherCenterY) < ALIGNMENT_THRESHOLD) {
        snappedY = otherCenterY - currentH / 2;
        guideY = otherCenterY;
      }
      // Top alignment
      if (Math.abs(snappedY - other.y) < ALIGNMENT_THRESHOLD) {
        snappedY = other.y;
        guideY = other.y;
      }
    }

    setAlignmentGuides({ x: guideX, y: guideY });
    return { x: Math.max(0, snappedX), y: Math.max(0, snappedY) };
  }, [canvasItems, snapToGrid]);

  // --- Drag items ---
  const handleItemMouseDown = (e: React.MouseEvent, itemId: string) => {
    if (editingEmployeeId || editingTasksId) return;
    if (toolMode === 'connect' || isSpaceDown) return;
    if (toolMode !== 'move') return;

    e.preventDefault();
    e.stopPropagation();

    const item = canvasItems.find(i => i.id === itemId);
    if (!item || !canvasContainerRef.current) return;

    const container = canvasContainerRef.current;
    const rect = container.getBoundingClientRect();

    setDragItemId(itemId);
    dragOffsetRef.current = {
      x: (e.clientX - rect.left + container.scrollLeft) / zoom - item.x,
      y: (e.clientY - rect.top + container.scrollTop) / zoom - item.y,
    };
    setIsDragging(true);
  };

  const handleCanvasMouseMove = useCallback((e: React.MouseEvent) => {
    // Panning with spacebar
    if (isPanning && canvasContainerRef.current) {
      const dx = e.clientX - panStartRef.current.x;
      const dy = e.clientY - panStartRef.current.y;
      canvasContainerRef.current.scrollLeft = panStartRef.current.scrollLeft - dx;
      canvasContainerRef.current.scrollTop = panStartRef.current.scrollTop - dy;
      return;
    }

    // Dragging items
    if (!isDragging || !dragItemId || !canvasContainerRef.current) return;

    const container = canvasContainerRef.current;
    const rect = container.getBoundingClientRect();
    const rawX = (e.clientX - rect.left + container.scrollLeft) / zoom - dragOffsetRef.current.x;
    const rawY = (e.clientY - rect.top + container.scrollTop) / zoom - dragOffsetRef.current.y;

    const { x, y } = snapPosition(rawX, rawY, dragItemId);

    setCanvasItems(prev => prev.map(item =>
      item.id === dragItemId ? { ...item, x, y } : item
    ));
  }, [isDragging, dragItemId, isPanning, zoom, snapPosition]);

  const handleCanvasMouseUp = useCallback(() => {
    if (isDragging) {
      setIsDragging(false);
      setDragItemId(null);
      setAlignmentGuides({ x: null, y: null });
    }
    if (isPanning) setIsPanning(false);
  }, [isDragging, isPanning]);

  const handleCanvasMouseDown = (e: React.MouseEvent) => {
    // Spacebar + click = pan
    if (isSpaceDown && canvasContainerRef.current) {
      e.preventDefault();
      setIsPanning(true);
      panStartRef.current = {
        x: e.clientX, y: e.clientY,
        scrollLeft: canvasContainerRef.current.scrollLeft,
        scrollTop: canvasContainerRef.current.scrollTop,
      };
    }
  };

  // Global mouse up
  useEffect(() => {
    const handler = () => { setIsDragging(false); setDragItemId(null); setIsPanning(false); setAlignmentGuides({ x: null, y: null }); };
    window.addEventListener('mouseup', handler);
    return () => window.removeEventListener('mouseup', handler);
  }, []);

  // --- Zoom ---
  useEffect(() => {
    const container = canvasContainerRef.current;
    if (!container) return;
    const handleWheel = (e: WheelEvent) => {
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
        setZoom(prev => Math.min(Math.max(prev + (e.deltaY > 0 ? -ZOOM_STEP : ZOOM_STEP), MIN_ZOOM), MAX_ZOOM));
      }
    };
    container.addEventListener('wheel', handleWheel, { passive: false });
    return () => container.removeEventListener('wheel', handleWheel);
  }, []);

  const handleZoomToFit = () => {
    if (!canvasContainerRef.current || canvasItems.length === 0) return;
    const rect = canvasContainerRef.current.getBoundingClientRect();
    let minX = Infinity, minY = Infinity, maxX = 0, maxY = 0;
    canvasItems.forEach(item => {
      const h = item.type === 'department' ? NODE_HEIGHT : EMPLOYEE_NODE_HEIGHT;
      minX = Math.min(minX, item.x); minY = Math.min(minY, item.y);
      maxX = Math.max(maxX, item.x + NODE_WIDTH); maxY = Math.max(maxY, item.y + h);
    });
    const cw = maxX - minX + 100, ch = maxY - minY + 100;
    setZoom(Math.min(Math.max(Math.min((rect.width - 40) / cw, (rect.height - 40) / ch), MIN_ZOOM), MAX_ZOOM));
  };

  // --- Sidebar drag & drop ---
  const handleSidebarDragStart = (e: React.DragEvent, type: 'department' | 'employee', data: Department | EmployeeWithProfile) => {
    e.dataTransfer.setData('application/json', JSON.stringify({ type, data, fromSidebar: true }));
    e.dataTransfer.effectAllowed = 'copy';
  };

  const handleCanvasDrop = (e: React.DragEvent) => {
    e.preventDefault();
    if (!canvasContainerRef.current) return;
    const container = canvasContainerRef.current;
    const rect = container.getBoundingClientRect();
    const x = (e.clientX - rect.left + container.scrollLeft) / zoom;
    const y = (e.clientY - rect.top + container.scrollTop) / zoom;
    try {
      const data = JSON.parse(e.dataTransfer.getData('application/json'));
      if (data.fromSidebar) {
        let snappedX = x - NODE_WIDTH / 2;
        let snappedY = y - (data.type === 'department' ? NODE_HEIGHT : EMPLOYEE_NODE_HEIGHT) / 2;
        if (snapToGrid) {
          snappedX = Math.round(snappedX / GRID_SIZE) * GRID_SIZE;
          snappedY = Math.round(snappedY / GRID_SIZE) * GRID_SIZE;
        }
        setCanvasItems(prev => [...prev, {
          id: data.data.id, type: data.type,
          x: Math.max(0, snappedX), y: Math.max(0, snappedY),
          data: data.data,
        }]);
        toast.success("Element hinzugefügt");
      }
    } catch {}
  };

  // --- Item actions ---
  const handleItemClick = (e: React.MouseEvent, itemId: string) => {
    e.stopPropagation();
    if (toolMode === 'select') setSelectedItemId(selectedItemId === itemId ? null : itemId);
  };

  const handleCanvasClick = (e: React.MouseEvent) => {
    if (e.target === canvasRef.current || (e.target as HTMLElement).tagName === 'svg') {
      setSelectedItemId(null); setConnectingFrom(null);
    }
  };

  const handleRemoveFromCanvas = (itemId: string) => {
    setCanvasItems(prev => prev.filter(i => i.id !== itemId));
    setConnections(prev => prev.filter(c => c.fromId !== itemId && c.toId !== itemId));
    if (selectedItemId === itemId) setSelectedItemId(null);
  };

  // --- Connections ---
  const handleStartConnection = (e: React.MouseEvent, id: string, side: ConnectionSide) => {
    e.stopPropagation(); e.preventDefault();
    setConnectingFrom({ id, side });
  };

  const handleCompleteConnection = (e: React.MouseEvent, toId: string, toSide: ConnectionSide) => {
    e.stopPropagation(); e.preventDefault();
    if (!connectingFrom || connectingFrom.id === toId) { setConnectingFrom(null); return; }
    const exists = connections.some(c =>
      (c.fromId === connectingFrom.id && c.toId === toId) || (c.fromId === toId && c.toId === connectingFrom.id)
    );
    if (!exists) {
      setConnections(prev => [...prev, { fromId: connectingFrom.id, toId, fromSide: connectingFrom.side, toSide }]);
      toast.success("Verbindung erstellt");
    }
    setConnectingFrom(null);
  };

  const handleRemoveConnection = (fromId: string, toId: string) => {
    setConnections(prev => prev.filter(c => !(c.fromId === fromId && c.toId === toId) && !(c.fromId === toId && c.toId === fromId)));
  };

  // --- Department CRUD ---
  const handleAddDepartment = async (name: string) => {
    try {
      await supabase.from("departments").insert({ name });
      toast.success(`"${name}" erstellt`);
      loadDepartments();
    } catch (e: any) {
      toast.error(e.code === '23505' ? "Abteilung existiert bereits" : "Fehler beim Erstellen");
    }
  };

  const handleDeleteDepartment = async () => {
    if (!deptToDelete) return;
    try {
      await supabase.from("employees").update({ department: null }).eq("department", deptToDelete.name);
      await supabase.from("departments").update({ parent_department_id: null }).eq("parent_department_id", deptToDelete.id);
      await supabase.from("departments").delete().eq("id", deptToDelete.id);
      handleRemoveFromCanvas(deptToDelete.id);
      toast.success(`"${deptToDelete.name}" gelöscht`);
      setDeleteDialogOpen(false); setDeptToDelete(null);
      loadDepartments(); onEmployeeUpdate();
    } catch { toast.error("Fehler beim Löschen"); }
  };

  // --- Position editing ---
  const handleSavePosition = async (e: React.MouseEvent, empId: string) => {
    e.stopPropagation();
    if (!editPosition.trim()) return;
    try {
      await supabase.from("employees").update({ position: editPosition.trim() }).eq("id", empId);
      setCanvasItems(prev => prev.map(item =>
        item.id === empId && item.type === 'employee'
          ? { ...item, data: { ...item.data as EmployeeWithProfile, position: editPosition.trim() } }
          : item
      ));
      toast.success("Position aktualisiert");
      setEditingEmployeeId(null); onEmployeeUpdate();
    } catch { toast.error("Fehler beim Speichern"); }
  };

  // --- Tasks editing ---
  const handleSaveTasks = (e: React.MouseEvent, itemId: string) => {
    e.stopPropagation();
    setCanvasItems(prev => prev.map(item => item.id === itemId ? { ...item, tasks: editTasksText.trim() } : item));
    setEditingTasksId(null);
  };

  // --- Cursor ---
  const getCursor = () => {
    if (isPanning || isSpaceDown) return 'grab';
    if (isDragging) return 'grabbing';
    if (toolMode === 'move') return 'default';
    if (toolMode === 'connect') return 'crosshair';
    return 'default';
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-200px)]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <span className="ml-2 text-muted-foreground">Organigramm wird geladen...</span>
      </div>
    );
  }

  return (
    <TooltipProvider>
      <div className="flex gap-3 h-[calc(100vh-200px)]">
        {/* Sidebar */}
        {!isEmployee && (
          <OrgChartSidebar
            departments={departments}
            employees={employees}
            itemsOnCanvas={itemsOnCanvas}
            onDragStart={handleSidebarDragStart}
            onAddDepartment={handleAddDepartment}
            onDeleteDepartment={(dept) => { setDeptToDelete(dept); setDeleteDialogOpen(true); }}
          />
        )}

        {/* Main Canvas Area */}
        <Card className="flex-1 overflow-hidden border-0 shadow-xl rounded-2xl bg-card/95 backdrop-blur-sm">
          {/* Toolbar */}
          <OrgChartToolbar
            toolMode={toolMode}
            setToolMode={(m) => { setToolMode(m); if (m !== 'connect') setConnectingFrom(null); }}
            zoom={zoom}
            onZoomIn={() => setZoom(prev => Math.min(prev + ZOOM_STEP, MAX_ZOOM))}
            onZoomOut={() => setZoom(prev => Math.max(prev - ZOOM_STEP, MIN_ZOOM))}
            onZoomReset={() => setZoom(1)}
            onZoomToFit={handleZoomToFit}
            isSaving={isSaving}
            hasUnsavedChanges={hasUnsavedChanges}
            onSave={handleSaveLayout}
            connectingFrom={!!connectingFrom}
            onCancelConnect={() => setConnectingFrom(null)}
            showHelp={showHelp}
            onToggleHelp={() => setShowHelp(!showHelp)}
            snapToGrid={snapToGrid}
            onToggleSnap={() => setSnapToGrid(!snapToGrid)}
            connectionCount={connections.length}
            isReadOnly={isEmployee}
          />

          {/* Help panel */}
          {!isEmployee && showHelp && (
            <div className="mx-4 mt-2 p-3 bg-muted/40 rounded-xl text-xs space-y-1.5 border border-border/30">
              <p className="font-semibold text-foreground">Tastenkürzel & Tipps:</p>
              <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-muted-foreground">
                <span><kbd className="px-1.5 py-0.5 bg-muted rounded text-[10px] font-mono">Leertaste</kbd> + Drag = Canvas verschieben</span>
                <span><kbd className="px-1.5 py-0.5 bg-muted rounded text-[10px] font-mono">Strg</kbd> + Mausrad = Zoom</span>
                <span><kbd className="px-1.5 py-0.5 bg-muted rounded text-[10px] font-mono">M</kbd> = Verschieben</span>
                <span><kbd className="px-1.5 py-0.5 bg-muted rounded text-[10px] font-mono">S</kbd> = Auswählen</span>
                <span><kbd className="px-1.5 py-0.5 bg-muted rounded text-[10px] font-mono">C</kbd> = Verbinden</span>
                <span><kbd className="px-1.5 py-0.5 bg-muted rounded text-[10px] font-mono">Esc</kbd> = Abbrechen</span>
                <span>Doppelklick auf Mitarbeiter = Details öffnen</span>
                <span>Klick auf Linie = Verbindung löschen</span>
              </div>
            </div>
          )}

          {/* Canvas */}
          <CardContent
            ref={canvasContainerRef}
            className="p-0 overflow-auto h-full"
            style={{ cursor: getCursor() }}
            onMouseDown={handleCanvasMouseDown}
          >
            <div
              ref={canvasRef}
              className="relative origin-top-left"
              style={{
                width: canvasSize.width,
                height: canvasSize.height,
                transform: `scale(${zoom})`,
                transformOrigin: 'top left',
                minWidth: `${100 / zoom}%`,
                minHeight: `${100 / zoom}%`,
              }}
              onDragOver={(e) => e.preventDefault()}
              onDrop={handleCanvasDrop}
              onMouseMove={handleCanvasMouseMove}
              onMouseUp={handleCanvasMouseUp}
              onClick={handleCanvasClick}
            >
              {/* Grid pattern */}
              <svg className="absolute inset-0 w-full h-full pointer-events-none" style={{ width: canvasSize.width, height: canvasSize.height }}>
                <defs>
                  <pattern id="smallGrid" width={GRID_SIZE} height={GRID_SIZE} patternUnits="userSpaceOnUse">
                    <path d={`M ${GRID_SIZE} 0 L 0 0 0 ${GRID_SIZE}`} fill="none" stroke="hsl(var(--border))" strokeWidth="0.5" opacity="0.3" />
                  </pattern>
                  <pattern id="largeGrid" width={GRID_SIZE * 5} height={GRID_SIZE * 5} patternUnits="userSpaceOnUse">
                    <rect width={GRID_SIZE * 5} height={GRID_SIZE * 5} fill="url(#smallGrid)" />
                    <path d={`M ${GRID_SIZE * 5} 0 L 0 0 0 ${GRID_SIZE * 5}`} fill="none" stroke="hsl(var(--border))" strokeWidth="1" opacity="0.2" />
                  </pattern>
                </defs>
                <rect width="100%" height="100%" fill="url(#largeGrid)" />
              </svg>

              {/* Alignment guides */}
              {alignmentGuides.x !== null && (
                <div
                  className="absolute top-0 w-px bg-primary/50 pointer-events-none z-30"
                  style={{ left: alignmentGuides.x, height: canvasSize.height }}
                />
              )}
              {alignmentGuides.y !== null && (
                <div
                  className="absolute left-0 h-px bg-primary/50 pointer-events-none z-30"
                  style={{ top: alignmentGuides.y, width: canvasSize.width }}
                />
              )}

              {/* Connections */}
              <OrgChartConnections
                canvasItems={canvasItems}
                connections={connections}
                canvasWidth={canvasSize.width}
                canvasHeight={canvasSize.height}
                onRemoveConnection={handleRemoveConnection}
              />

              {/* Canvas Items */}
              {canvasItems.map(item => {
                const isSelected = selectedItemId === item.id;
                const isBeingDragged = dragItemId === item.id;
                const isConnecting = connectingFrom?.id === item.id;

                if (item.type === 'department') {
                  return (
                    <DepartmentCard
                      key={item.id}
                      item={item}
                      dept={item.data as Department}
                      isSelected={isSelected}
                      isBeingDragged={isBeingDragged}
                      isConnecting={isConnecting}
                      isReadOnly={isEmployee}
                      toolMode={toolMode}
                      connectingFromId={connectingFrom?.id || null}
                      connectingFromSide={connectingFrom?.side || null}
                      onMouseDown={(e) => !isEmployee && handleItemMouseDown(e, item.id)}
                      onClick={(e) => !isEmployee && handleItemClick(e, item.id)}
                      onRemove={() => handleRemoveFromCanvas(item.id)}
                      onStartConnection={handleStartConnection}
                      onCompleteConnection={handleCompleteConnection}
                    />
                  );
                } else {
                  const emp = item.data as EmployeeWithProfile;
                  return (
                    <EmployeeCard
                      key={item.id}
                      item={item}
                      emp={emp}
                      isSelected={isSelected}
                      isBeingDragged={isBeingDragged}
                      isConnecting={isConnecting}
                      isReadOnly={isEmployee}
                      toolMode={toolMode}
                      connectingFromId={connectingFrom?.id || null}
                      connectingFromSide={connectingFrom?.side || null}
                      editingEmployeeId={editingEmployeeId}
                      editPosition={editPosition}
                      editingTasksId={editingTasksId}
                      editTasksText={editTasksText}
                      onMouseDown={(e) => !isEmployee && handleItemMouseDown(e, item.id)}
                      onClick={(e) => !isEmployee && handleItemClick(e, item.id)}
                      onDoubleClick={() => navigate(`/employee/${emp.id}`)}
                      onRemove={() => handleRemoveFromCanvas(item.id)}
                      onStartEditPosition={(e) => { e.stopPropagation(); setEditingEmployeeId(emp.id); setEditPosition(emp.position); }}
                      onSavePosition={(e) => handleSavePosition(e, emp.id)}
                      onCancelEdit={(e) => { e.stopPropagation(); setEditingEmployeeId(null); }}
                      onEditPositionChange={setEditPosition}
                      onStartEditTasks={(e) => { e.stopPropagation(); setEditingTasksId(item.id); setEditTasksText(item.tasks || ''); }}
                      onSaveTasks={(e) => handleSaveTasks(e, item.id)}
                      onCancelEditTasks={(e) => { e.stopPropagation(); setEditingTasksId(null); }}
                      onEditTasksChange={setEditTasksText}
                      onStartConnection={handleStartConnection}
                      onCompleteConnection={handleCompleteConnection}
                    />
                  );
                }
              })}

              {/* Empty states */}
              {!isEmployee && canvasItems.length === 0 && (
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <div className="text-center text-muted-foreground/60">
                    <Move className="h-16 w-16 mx-auto mb-4 opacity-30" />
                    <p className="text-lg font-semibold">Ziehen Sie Elemente hierher</p>
                    <p className="text-sm mt-1">Abteilungen und Mitarbeiter von der Sidebar auf das Canvas ziehen</p>
                  </div>
                </div>
              )}
              {isEmployee && canvasItems.length === 0 && (
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <div className="text-center text-muted-foreground/60">
                    <Building2 className="h-16 w-16 mx-auto mb-4 opacity-30" />
                    <p className="text-lg font-semibold">Noch kein Organigramm erstellt</p>
                    <p className="text-sm mt-1">Ihr Administrator wird das Organigramm erstellen</p>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Delete dialog */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Abteilung löschen?</AlertDialogTitle>
              <AlertDialogDescription>
                Möchten Sie die Abteilung "{deptToDelete?.name}" wirklich löschen?
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Abbrechen</AlertDialogCancel>
              <AlertDialogAction onClick={handleDeleteDepartment} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                Löschen
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </TooltipProvider>
  );
}
